import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { InputValidationError } from "@src/types/timeline";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { firebase_auth } from "@src/utility_functions/database_operations/config/firebaseConfig";
import {
  checkIfPasswordsMatch,
  validateEmail,
  validatePassword,
} from "#shared/inputValidation/inputValidation.js";
import { FederatedLoginButton } from "@src/components/Buttons/FederatedLoginButton";
import { OrSectionSeparator } from "@src/components/Forms/FormElements/OrSectionSeparator";
import { TextInput } from "@src/components/Forms/FormElements/Inputs/TextInput";
import { PasswordInput } from "@src/components/Forms/FormElements/Inputs/PasswordInput";
import { validateInput } from "@src/utility_functions/inputValidation/validateInput";
import { fetchFirestoreUserObjectFromEmail } from "@src/utility_functions/database_operations/users/readUser/fetchFirestoreUserObjectFromEmail";
import { createUser } from "@src/utility_functions/database_operations/users/createUser";
import { LoginForm } from "@src/components/Forms/User/loginUser/LoginForm";
import { login_with_google } from "@src/utility_functions/database_operations/users/federated_logins/google/login_with_google";
import { useModal } from "@src/utility_functions/customHooks/useModal";

export const RegistrationForm = (_props) => {
  const { openDialog, closeDialog, setModalContent } = useModal();
  const queryClient = useQueryClient();
  const [emailAddressError, setEmailAddressError] =
    useState<InputValidationError>({
      errorClass: "",
      errorMessages: [],
    });
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<InputValidationError>({
    errorClass: "",
    errorMessages: [],
  });
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [confirmedPasswordError, setConfirmedPasswordError] =
    useState<InputValidationError>({
      errorClass: "",
      errorMessages: [],
    });

  return (
    <>
      <FederatedLoginButton
        logo="/images/logos/google_login_button_icon.jpg"
        text="Sign up with Google"
        size="large"
        handleClick={async () => {
          const serverResponse = await login_with_google();
          queryClient.invalidateQueries({
            queryKey: ["userDocument", serverResponse.id, serverResponse.id],
          });
          closeDialog();
        }}
        buttonDisabled={false}
      />
      <OrSectionSeparator case="lower" className="" />
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          const formData = new FormData(event.currentTarget);
          const formDataEntries = Object.fromEntries(formData.entries());
          const emailValid =
            validateEmail(formDataEntries.email).errorClass === "valid";
          const passwordValid =
            validatePassword(formDataEntries.password).errorClass === "valid";
          const confirmedPasswordValid =
            validatePassword(formDataEntries.secondaryPassword).errorClass ===
            "valid";
          const passWordsMatch =
            checkIfPasswordsMatch([
              formDataEntries.password,
              formDataEntries.secondaryPassword,
            ]).errorClass === "valid";
          const formValid =
            emailValid &&
            passwordValid &&
            confirmedPasswordValid &&
            passWordsMatch;
          if (formValid) {
            const user_email_exists = await fetchFirestoreUserObjectFromEmail(
              String(formDataEntries.email),
            );
            if (user_email_exists === undefined) {
              try {
                await createUserWithEmailAndPassword(
                  firebase_auth,
                  String(formDataEntries.email),
                  password,
                );
                await sendEmailVerification(firebase_auth?.currentUser);
                try {
                  const serverResponse = await createUser();
                  localStorage.setItem("loginMethod", "password");
                  queryClient.invalidateQueries({
                    queryKey: [
                      "userDocument",
                      serverResponse.userData.id,
                      serverResponse.userData.id,
                    ],
                  });
                  closeDialog();
                } catch (error) {
                  console.warn(error);
                }
              } catch (error) {
                if (
                  typeof error === "object" &&
                  error !== null &&
                  "code" in error &&
                  typeof (error as any).code === "string"
                ) {
                  const condition1 =
                    (error as any).code === "auth/email-already-in-use";
                  const condition2 =
                    (error as any).code === "auth/invalid-email";

                  if (condition1) {
                    setEmailAddressError({
                      errorClass: "semi_valid",
                      errorMessages: [
                        "An account with this email already exists",
                      ],
                    });
                  } else if (condition2) {
                    setEmailAddressError({
                      errorClass: "semi_valid",
                      errorMessages: ["Please enter a valid email address"],
                    });
                  }
                }
                return;
              }
            } else {
              try {
                await signInWithEmailAndPassword(
                  firebase_auth,
                  String(formDataEntries.email),
                  password,
                );
                closeDialog();
              } catch {
                setEmailAddressError({
                  errorClass: "semi_valid",
                  errorMessages: ["Invalid email or password"],
                });
                setPasswordError({
                  errorClass: "semi_valid",
                  errorMessages: ["Invalid email or password"],
                });
              }
            }
          } else {
            validateInput(
              formDataEntries.email,
              validateEmail,
              setEmailAddressError,
            );
            validateInput(
              formDataEntries.password,
              validatePassword,
              setPasswordError,
            );
            const confirmedPasswordValidationObject = validateInput(
              formDataEntries.secondaryPassword,
              validatePassword,
              setConfirmedPasswordError,
            );
            if (confirmedPasswordValidationObject) {
              validateInput(
                [formDataEntries.password, formDataEntries.secondaryPassword],
                checkIfPasswordsMatch,
                setConfirmedPasswordError,
              );
            }
          }
        }}
      >
        <TextInput
          className="_margin-top--1rem _margin-bottom--1rem"
          inputID="emailInput"
          name="email"
          inputType="email"
          labelText="Email"
          maxlength={50}
          defaultValue=""
          placeholder="Email"
          autocomplete="email"
          required={true}
          validationResponse={emailAddressError}
          onChangeFunction={(event: React.FocusEvent<HTMLInputElement>) => {
            validateInput(
              event.target.value,
              validateEmail,
              setEmailAddressError,
            );
          }}
        />
        <PasswordInput
          className="_margin-bottom--1rem"
          inputID="passwordInput"
          name="password"
          inputType="password"
          labelText="Password"
          maxlength={50}
          defaultValue=""
          placeholder="Password"
          autocomplete="new-password"
          required={true}
          validationResponse={passwordError}
          onChange={(event: React.FocusEvent<HTMLInputElement>) => {
            setPassword(event.target.value);
            validateInput(
              event.target.value,
              validatePassword,
              setPasswordError,
            );
            const confirmedPasswordValidationObject = validateInput(
              confirmedPassword,
              validatePassword,
              setConfirmedPasswordError,
            );
            if (confirmedPasswordValidationObject) {
              validateInput(
                [event.target.value, confirmedPassword],
                checkIfPasswordsMatch,
                setConfirmedPasswordError,
              );
            }
          }}
        />
        <PasswordInput
          className="_margin-bottom--1rem"
          inputID="secondaryPasswordInput"
          name="secondaryPassword"
          inputType="password"
          labelText="Confirm Password"
          maxlength={50}
          defaultValue=""
          placeholder="Confirm Password"
          autocomplete="new-password"
          required={true}
          validationResponse={confirmedPasswordError}
          onChange={(event: React.FocusEvent<HTMLInputElement>) => {
            setConfirmedPassword(event.target.value);
            const passwordValidationObject = validateInput(
              event.target.value,
              validatePassword,
              setConfirmedPasswordError,
            );
            if (passwordValidationObject) {
              validateInput(
                [event.target.value, password],
                checkIfPasswordsMatch,
                setConfirmedPasswordError,
              );
            }
          }}
        />
        <p className="_font-size--14px">
          A verification email will be sent to the email address provided above
        </p>
        <button
          type="submit"
          className="btn btn--primary btn--block _margin-top--1rem"
        >
          Create Account
        </button>
        <hr className="_margin-top--1rem _margin-bottom--1rem" />
        <p className="_display--inline-block _font-size--14px">
          Already have an account?&nbsp;
          <button
            style={{ color: "var(--blue-30)", fontSize: "14px" }}
            onClick={() => {
              openDialog();
              setModalContent({
                modalTitle: "Log In",
                dialogWidth: "50%",
                modalContent: (
                  <div>
                    <LoginForm />
                  </div>
                ),
              });
            }}
          >
            Log In
          </button>
        </p>
      </form>
    </>
  );
};
